import { Box, Divider, Stack, Typography } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { ReviewMode } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { DateTime } from "luxon";
import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import { PriorityOrderSwitch } from "./PriorityOrderSwitch";
import { ReviewersEditList, ReviewerPicker, Reviewer } from "./ReviewersEditList";

gql(`
fragment SubmissionReviewerPickerUser on User {
    id
    email
    person {
      first
      last
    }
  }
`);

gql(`
  fragment SubmissionReviewerPicker on SubmissionReviewer {
    id
    dueDate
    reviewOrder
    isStaff
    user {
      ...SubmissionReviewerPickerUser
    }
  }
`);

export interface SubmissionReviewerFormData {
  userId: string;
  reviewOrder: number;
  isRequired: boolean;
  isSigner: boolean;
  dueDate: DateTime;
}

interface ReviewerManagementDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (reviewers: Reviewer[], reviewMode: ReviewMode, peerReviewRequired?: boolean) => void;
  onChange?: (reviewers: Reviewer[], reviewMode: ReviewMode) => void;
  initialReviewers?: Reviewer[];
  initialReviewMode?: ReviewMode;
  /**
   * Treat an empty staff "Peer Reviewer" picker as the open peer-review queue
   * (any Encamp staff can claim it) rather than as no peer review. The stepper
   * opts into this because its "Require peer review" switch already establishes
   * the requirement; the legacy submission form leaves it off. When on, `onSave`
   * receives the open-queue flag as its third argument.
   */
  showEncampStaffPeerReview?: boolean;
  initialPeerReviewRequired?: boolean;
  submissionFacilityIds?: string[];
}

export function ReviewerManagementDialog({
  open,
  onClose,
  onSave,
  onChange,
  initialReviewers = [],
  initialReviewMode = ReviewMode.Parallel,
  showEncampStaffPeerReview = false,
  initialPeerReviewRequired = false,
  submissionFacilityIds = [],
}: ReviewerManagementDialogProps) {
  // Store initial snapshot for cancel/revert
  const initialSnapshotRef = useRef({
    reviewers: initialReviewers,
    reviewMode: initialReviewMode,
  });

  const [reviewers, setReviewers] = useState<Reviewer[]>(() => {
    // Filter out staff reviewers from regular reviewers
    const nonStaffReviewers = initialReviewers.filter((r) => !r.isStaff);
    return nonStaffReviewers.length > 0
      ? nonStaffReviewers
      : [{ id: uuidv4(), name: "", email: "", order: 1, isStaff: false }];
  });
  const [staffReviewer, setStaffReviewer] = useState<Reviewer | null>(
    initialReviewers.find((r) => r.isStaff) ?? null
  );
  // Open-queue peer review (stepper only): "any Encamp staff can review".
  const [peerReviewEnabled, setPeerReviewEnabled] = useState(initialPeerReviewRequired);

  const [reviewMode, setReviewMode] = useState(initialReviewMode);

  const { isStaff } = useCurrentUser();

  // Whether peer review is required at all is decided outside this dialog (the
  // stepper's "Require peer review" switch, which a named staff reviewer also
  // satisfies). Read it once so clearing the picker returns to the open queue
  // instead of silently dropping the requirement.
  const peerReviewIsRequiredRef = useRef(
    initialPeerReviewRequired || initialReviewers.some((r) => r.isStaff && !r.isSigner)
  );

  // Naming someone makes them a real reviewer row; leaving the picker empty
  // keeps peer review open for any staff member to claim. The empty state is
  // what "any Encamp staff" looks like, so the picker never pre-fills a
  // placeholder person that would have to be cleared first.
  const handlePeerReviewerChange = (reviewer: Reviewer | null) => {
    setStaffReviewer(reviewer);
    setPeerReviewEnabled(reviewer === null && peerReviewIsRequiredRef.current);
  };

  // Helper to build valid reviewers without mutating
  const buildValidReviewers = (
    currentReviewers: Reviewer[],
    currentStaffReviewer: Reviewer | null
  ): Reviewer[] => {
    let validReviewers = currentReviewers.filter((r) => r.name.trim());

    // Renumber without mutation
    validReviewers = validReviewers.map((r, index) => ({
      ...r,
      order: index + 1,
    }));

    // Add staff reviewer if valid
    if (currentStaffReviewer && currentStaffReviewer.email.trim()) {
      const staffWithOrder = { ...currentStaffReviewer, order: 0 };
      validReviewers = [staffWithOrder, ...validReviewers];
    }

    return validReviewers;
  };

  // Notify parent when changes happen - using ref to avoid adding to deps
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!onChangeRef.current) return;

    const validReviewers = buildValidReviewers(reviewers, staffReviewer);
    onChangeRef.current(validReviewers, reviewMode);
  }, [reviewers, staffReviewer, reviewMode]);

  const handleCancel = () => {
    // Revert to initial snapshot
    if (onChange) {
      onChange(initialSnapshotRef.current.reviewers, initialSnapshotRef.current.reviewMode);
    }
    onClose();
  };

  const handleSave = () => {
    // Validate that all reviewers have name
    let validReviewers = reviewers.filter((r) => r.name.trim());

    // non staff reviewers will have order of 1, 2, 3, etc.
    // and we want to condense the indexes down and skip any blank reviewers
    validReviewers.forEach((r, index) => {
      r.order = index + 1;
    });

    // staff reviewers will have order of 0, always first
    if (staffReviewer && staffReviewer.email.trim()) {
      staffReviewer.order = 0;
      validReviewers = [staffReviewer, ...validReviewers];
    }

    // With the Encamp-staff peer-review queue on, a config of "peer review only,
    // no named reviewers" is valid, so don't block an otherwise-empty save.
    if (validReviewers.length === 0 && !(showEncampStaffPeerReview && peerReviewEnabled)) {
      return;
    }

    // `peerReviewRequired` means "peer review is needed", so naming someone doesn't clear it.
    const staffReviewerNamed = !!(staffReviewer && staffReviewer.email.trim());
    onSave(
      validReviewers,
      reviewMode,
      showEncampStaffPeerReview ? peerReviewEnabled || staffReviewerNamed : undefined
    );
  };

  const canSave =
    reviewers.some((r) => r.name.trim()) ||
    !!(staffReviewer && staffReviewer.email.trim()) ||
    (showEncampStaffPeerReview && peerReviewEnabled);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        title="Add Reviewer"
        description={
          <>
            Add one or more reviewers with the option to assign an order. These reviewers will be
            emailed the submission to sign off before handing to the next reviewer and lastly, the
            signer. If no priority order is selected, all reviewers will be emailed simultaneously.
          </>
        }
        primaryActionButtonProps={{
          saveText: "SAVE",
          onClick: handleSave,
          disabled: !canSave,
        }}
        cancelButtonProps={{
          text: "CANCEL",
          onClick: handleCancel,
        }}
      >
        <Stack spacing={3}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PriorityOrderSwitch value={reviewMode} onChange={setReviewMode} labelSize="16px" />
          </Box>
          {isStaff && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography variant="body1" sx={{ flexShrink: 0 }}>
                  Peer Reviewer
                </Typography>
                <Box component="span" sx={{ display: "flex", flex: 1 }}>
                  <ReviewerPicker
                    staffOnly={true}
                    placeholder={showEncampStaffPeerReview ? "Any Encamp Staff member" : undefined}
                    onChange={handlePeerReviewerChange}
                    value={staffReviewer}
                  />
                </Box>
              </Box>
              {showEncampStaffPeerReview && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Choose a specific Encamp staff reviewer, or leave this empty to let any staff
                  member claim the peer review.
                </Typography>
              )}
              <Divider sx={{ mt: 3 }} />
            </Box>
          )}

          {/* Reviewers List */}
          <ReviewersEditList
            reviewers={reviewers.filter((r) => !r.isStaff)}
            setReviewers={setReviewers}
            rowReordering={reviewMode === ReviewMode.Sequential}
            submissionFacilityIds={submissionFacilityIds}
          />
        </Stack>
      </Dialog>
    </LocalizationProvider>
  );
}
